/**
 * Retention Guidance Panel
 * MIGRATED: MUI -> Tailwind CSS components
 *
 * User-friendly guidance summary explaining:
 * - How churn prediction works
 * - What risk levels mean
 * - Concrete action plans for member engagement
 * - Best practices for outreach
 *
 * Trust Marketplace - Member Retention Intelligence
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ChevronDown,
  Brain,
  HelpCircle,
  Phone,
  MessageSquare,
  Mail,
  Home,
  Clock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Stethoscope,
  Users,
  ClipboardList,
  Gauge,
  ShieldCheck,
  X,
} from 'lucide-react';

interface RetentionGuidancePanelProps {
  variant?: 'full' | 'compact' | 'inline';
  context?: 'member' | 'network' | 'dashboard';
  onClose?: () => void;
}

// Risk level explanations
const riskLevelGuide = [
  {
    level: 'healthy',
    color: 'success',
    icon: <CheckCircle className="h-4 w-4" />,
    title: 'Healthy (Score 70-100)',
    description: 'Member is actively engaged with programs and responds well to communications.',
    badgeVariant: 'default' as const,
    badgeClassName: 'bg-green-100 text-green-800 hover:bg-green-100',
    actions: [
      'Maintain regular check-in cadence (monthly)',
      'Celebrate milestones and achievements',
      'Ask for feedback to improve experience'
    ]
  },
  {
    level: 'watch',
    color: 'info',
    icon: <Info className="h-4 w-4" />,
    title: 'Watch (Score 50-69)',
    description: 'Member shows some engagement gaps but remains reachable. Early intervention recommended.',
    badgeVariant: 'default' as const,
    badgeClassName: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    actions: [
      'Increase contact frequency (bi-weekly)',
      'Personalize outreach with program updates',
      'Address any barriers to participation'
    ]
  },
  {
    level: 'at_risk',
    color: 'warning',
    icon: <AlertTriangle className="h-4 w-4" />,
    title: 'At Risk (Score 30-49)',
    description: 'Member engagement is declining. Proactive intervention needed to prevent dropout.',
    badgeVariant: 'default' as const,
    badgeClassName: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
    actions: [
      'Schedule immediate wellness check-in',
      'Try multiple contact methods',
      'Connect with case manager for assessment',
      'Review SDOH factors that may be impacting engagement'
    ]
  },
  {
    level: 'critical',
    color: 'error',
    icon: <AlertCircle className="h-4 w-4" />,
    title: 'Critical (Score 0-29)',
    description: 'Member is at high risk of disengagement. Urgent intervention required.',
    badgeVariant: 'destructive' as const,
    badgeClassName: 'bg-red-100 text-red-800 hover:bg-red-100',
    actions: [
      'Prioritize within 24-48 hours',
      'Consider home visit if contact fails',
      'Escalate to supervisor if unreachable',
      'Check for address/phone updates needed',
      'Connect with community health worker'
    ]
  },
  {
    level: 'lost',
    color: 'default',
    icon: <AlertCircle className="h-4 w-4" />,
    title: 'Lost',
    description: 'Member has been unreachable for extended period. Recovery outreach needed.',
    badgeVariant: 'secondary' as const,
    badgeClassName: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    actions: [
      'Attempt contact via all channels',
      'Check with emergency contacts',
      'Verify contact information accuracy',
      'Send certified mail if applicable',
      'Document all recovery attempts'
    ]
  }
];

// Churn prediction explanation
const churnPredictionGuide = {
  title: 'Understanding AI Churn Prediction',
  description: `Our AI model analyzes multiple factors to predict the likelihood that a member will
    disengage from their program within the next 14 days. This prediction helps you prioritize
    outreach to members who need attention most.`,
  factors: [
    {
      name: 'Contact History',
      weight: '20-25%',
      description: 'Days since last successful contact and response patterns',
      icon: <Phone className="h-4 w-4" />
    },
    {
      name: 'Engagement Trend',
      weight: '15-20%',
      description: 'Whether member engagement is improving, stable, or declining',
      icon: <TrendingUp className="h-4 w-4" />
    },
    {
      name: 'Activity Level',
      weight: '15-18%',
      description: 'Participation in program activities, appointments, and milestones',
      icon: <ClipboardList className="h-4 w-4" />
    },
    {
      name: 'Communication Success',
      weight: '10-15%',
      description: 'Response rates to SMS, calls, and emails',
      icon: <MessageSquare className="h-4 w-4" />
    },
    {
      name: 'Social Factors (SDOH)',
      weight: '10-12%',
      description: 'Area-level factors like transportation access, poverty rate, and healthcare availability',
      icon: <Users className="h-4 w-4" />
    },
    {
      name: 'Data Quality',
      weight: '8-10%',
      description: 'Completeness and accuracy of member contact information',
      icon: <ShieldCheck className="h-4 w-4" />
    }
  ],
  thresholds: [
    { range: '0-20%', level: 'Low Risk', color: 'success', action: 'Continue regular engagement', className: 'bg-green-100 text-green-800' },
    { range: '20-40%', level: 'Moderate Risk', color: 'info', action: 'Increase contact frequency', className: 'bg-blue-100 text-blue-800' },
    { range: '40-70%', level: 'High Risk', color: 'warning', action: 'Schedule immediate outreach', className: 'bg-yellow-100 text-yellow-800' },
    { range: '70-100%', level: 'Critical Risk', color: 'error', action: 'Urgent intervention within 24h', className: 'bg-red-100 text-red-800' }
  ]
};

// Best practices for outreach
const outreachBestPractices = [
  {
    channel: 'Phone Call',
    icon: <Phone className="h-5 w-5" />,
    bestFor: 'Complex discussions, wellness checks, sensitive topics',
    tips: [
      'Call during member\'s optimal hours (shown in profile)',
      'Leave detailed voicemail with callback number',
      'Try 3 attempts at different times before escalating',
      'Document outcome of each attempt'
    ]
  },
  {
    channel: 'SMS/Text',
    icon: <MessageSquare className="h-5 w-5" />,
    bestFor: 'Quick reminders, appointment confirmations, simple questions',
    tips: [
      'Keep messages under 160 characters',
      'Include clear call-to-action',
      'Send during daytime hours (9am-7pm)',
      'Follow up if no response in 48 hours'
    ]
  },
  {
    channel: 'Email',
    icon: <Mail className="h-5 w-5" />,
    bestFor: 'Detailed information, documents, program updates',
    tips: [
      'Use clear, specific subject lines',
      'Include important info in first paragraph',
      'Add phone number for questions',
      'Check for bounces and update address if needed'
    ]
  },
  {
    channel: 'Home Visit',
    icon: <Home className="h-5 w-5" />,
    bestFor: 'Critical members, verification needs, in-person assessments',
    tips: [
      'Schedule with member if possible',
      'Bring program materials and resources',
      'Document visit details and observations',
      'Leave door hanger if member not home'
    ]
  }
];

// Action plan templates
const actionPlanTemplates = {
  declining_engagement: {
    title: 'Declining Engagement Response',
    steps: [
      { step: 1, action: 'Review member profile for recent changes', timeframe: 'Day 1' },
      { step: 2, action: 'Attempt phone contact using preferred hours', timeframe: 'Day 1' },
      { step: 3, action: 'Send follow-up SMS if no answer', timeframe: 'Day 1' },
      { step: 4, action: 'Try alternative contact method', timeframe: 'Day 2' },
      { step: 5, action: 'Schedule wellness check or case review', timeframe: 'Day 3' },
      { step: 6, action: 'Escalate if still unreachable', timeframe: 'Day 5' }
    ]
  },
  contact_failures: {
    title: 'Multiple Contact Failures',
    steps: [
      { step: 1, action: 'Verify contact information accuracy', timeframe: 'Day 1' },
      { step: 2, action: 'Try all available channels (phone, SMS, email)', timeframe: 'Day 1-2' },
      { step: 3, action: 'Check emergency contacts if available', timeframe: 'Day 2' },
      { step: 4, action: 'Send certified letter for critical members', timeframe: 'Day 3' },
      { step: 5, action: 'Consider home visit if resources allow', timeframe: 'Day 5' },
      { step: 6, action: 'Update status and document attempts', timeframe: 'Day 7' }
    ]
  },
  new_high_risk: {
    title: 'New High-Risk Identification',
    steps: [
      { step: 1, action: 'Review prediction factors and recent history', timeframe: 'Same day' },
      { step: 2, action: 'Prioritize for immediate outreach', timeframe: 'Same day' },
      { step: 3, action: 'Conduct wellness check call', timeframe: 'Within 24h' },
      { step: 4, action: 'Assess barriers and SDOH needs', timeframe: 'Within 24h' },
      { step: 5, action: 'Create personalized engagement plan', timeframe: 'Within 48h' },
      { step: 6, action: 'Set follow-up reminder for 3 days', timeframe: 'Within 48h' }
    ]
  },
  sdoh_barriers: {
    title: 'Social Determinant Barriers',
    steps: [
      { step: 1, action: 'Identify specific SDOH factors affecting member', timeframe: 'Day 1' },
      { step: 2, action: 'Research available community resources', timeframe: 'Day 1' },
      { step: 3, action: 'Discuss barriers with member empathetically', timeframe: 'Day 1-2' },
      { step: 4, action: 'Provide warm referrals to relevant services', timeframe: 'Day 2' },
      { step: 5, action: 'Follow up on resource utilization', timeframe: 'Week 1' },
      { step: 6, action: 'Adjust engagement approach based on barriers', timeframe: 'Ongoing' }
    ]
  }
};

export const RetentionGuidancePanel: React.FC<RetentionGuidancePanelProps> = ({
  variant = 'full',
  context = 'dashboard',
  onClose,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange = (value: string) => {
    setExpanded(expanded === value ? false : value);
  };

  if (variant === 'inline') {
    return (
      <Alert className="mb-2 border-blue-200 bg-blue-50">
        <div className="flex items-start justify-between w-full">
          <div className="flex items-start gap-2">
            <Lightbulb className="h-4 w-4 text-blue-600 mt-0.5" />
            <AlertDescription className="text-sm">
              <strong>Quick Tip:</strong> Focus on members with declining trends or high churn probability first.
              Use the recommended contact channel and optimal time shown in each member's profile.
            </AlertDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1">
                  <HelpCircle className="h-4 w-4 text-blue-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Learn more about retention intelligence</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </Alert>
    );
  }

  return (
    <Card className="mb-3">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">
              Retention Intelligence Guide
            </h3>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-3">
          This guide explains how our AI-powered retention system works and provides actionable steps
          to keep members engaged with their programs.
        </p>

        <Accordion
          type="single"
          collapsible
          value={expanded || undefined}
          onValueChange={handleAccordionChange}
        >
          {/* Understanding Churn Prediction */}
          <AccordionItem value="churn">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                <span className="font-medium">Understanding AI Churn Prediction</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-sm mb-4">
                {churnPredictionGuide.description}
              </p>

              <h4 className="text-sm font-semibold mb-2 mt-4">
                Prediction Factors & Weights
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {churnPredictionGuide.factors.map((factor, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {factor.icon}
                      <span className="text-sm font-medium">
                        {factor.name}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {factor.weight}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {factor.description}
                    </p>
                  </div>
                ))}
              </div>

              <h4 className="text-sm font-semibold mb-2">
                Risk Thresholds & Recommended Actions
              </h4>
              <div className="flex flex-wrap gap-2">
                {churnPredictionGuide.thresholds.map((t, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className={t.className}
                  >
                    {t.range}: {t.level}
                  </Badge>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Risk Levels Explained */}
          <AccordionItem value="risk">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Gauge className="h-5 w-5 text-primary" />
                <span className="font-medium">Risk Levels Explained</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-1">
                {riskLevelGuide.map((risk, index) => (
                  <React.Fragment key={risk.level}>
                    <li className="flex flex-col py-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${risk.badgeClassName} inline-flex items-center gap-1`}>
                          {risk.icon}
                          {risk.title}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {risk.description}
                      </p>
                      <span className="text-xs font-semibold mb-1">
                        Recommended Actions:
                      </span>
                      <ul className="space-y-1 pl-4">
                        {risk.actions.map((action, actionIndex) => (
                          <li key={actionIndex} className="flex items-start gap-2 py-0.5">
                            <CheckCircle className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-xs">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                    {index < riskLevelGuide.length - 1 && (
                      <hr className="border-border my-2" />
                    )}
                  </React.Fragment>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>

          {/* Outreach Best Practices */}
          <AccordionItem value="outreach">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                <span className="font-medium">Outreach Best Practices</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {outreachBestPractices.map((practice, index) => (
                  <div key={index} className="border rounded-lg p-4 h-full">
                    <div className="flex items-center gap-2 mb-2">
                      {practice.icon}
                      <h4 className="text-sm font-semibold">{practice.channel}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      <strong>Best for:</strong> {practice.bestFor}
                    </p>
                    <ul className="space-y-1">
                      {practice.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start gap-2 py-0.5">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <span className="text-xs">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Action Plan Templates */}
          <AccordionItem value="actions">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                <span className="font-medium">Action Plan Templates</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(actionPlanTemplates).map(([key, plan]) => (
                  <div key={key} className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-2">
                      {plan.title}
                    </h4>
                    <ul className="space-y-2">
                      {plan.steps.map((step) => (
                        <li key={step.step} className="flex items-start gap-2">
                          <Badge variant="secondary" className="w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            {step.step}
                          </Badge>
                          <div className="flex-1 pt-0.5">
                            <p className="text-sm">{step.action}</p>
                            <p className="text-xs text-muted-foreground">{step.timeframe}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {variant === 'full' && (
          <div className="mt-4 p-4 bg-primary/10 rounded-lg">
            <p className="text-sm text-primary">
              <strong>Pro Tip:</strong> Sort at-risk members by churn probability and work down the list.
              Members with declining trends need attention even if their current score is still moderate.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RetentionGuidancePanel;
